/**
 * Context de Autenticación
 * Maneja el estado de autenticación del usuario, login, logout y refresh token
 */
import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import * as authService from '../services/authService';
import { useNotification } from './NotificationContext';

// Estado inicial
const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null,
};

// Tipos de acciones
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_ERROR: 'LOGIN_ERROR',
  LOGOUT: 'LOGOUT',
  REFRESH_TOKEN_SUCCESS: 'REFRESH_TOKEN_SUCCESS',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.accessToken,
        isAuthenticated: true,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.LOGIN_ERROR:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload,
      };

    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };

    case AUTH_ACTIONS.REFRESH_TOKEN_SUCCESS:
      return {
        ...state,
        token: action.payload.accessToken,
        isAuthenticated: true,
        error: null,
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
      };

    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };

    case AUTH_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };

    default:
      return state;
  }
};

// Crear contexto
const AuthContext = createContext(undefined);

// Provider
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { showSuccess, showError } = useNotification();

  /**
   * Inicializar autenticación desde localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      try {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

        const storedUser = authService.getStoredUser();
        const isAuth = authService.isAuthenticated();

        if (isAuth && storedUser) {
          // Verificar que el token siga siendo válido obteniendo usuario actual
          try {
            const currentUser = await authService.getCurrentUser();
            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: currentUser,
            });
          } catch (error) {
            // Si falla, usar el usuario del localStorage
            dispatch({
              type: AUTH_ACTIONS.SET_USER,
              payload: storedUser,
            });
          }
        } else {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      } catch (error) {
        console.error('Error al inicializar autenticación:', error);
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } finally {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initAuth();
  }, []);

  /**
   * Iniciar sesión
   */
  const login = useCallback(
    async (email, password) => {
      try {
        dispatch({ type: AUTH_ACTIONS.LOGIN_START });

        const data = await authService.login(email, password);

        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: data,
        });

        showSuccess('Sesión iniciada correctamente');
        return data;
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error al iniciar sesión';

        dispatch({
          type: AUTH_ACTIONS.LOGIN_ERROR,
          payload: errorMessage,
        });

        showError(errorMessage);
        throw error;
      }
    },
    [showSuccess, showError]
  );

  /**
   * Cerrar sesión
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      showSuccess('Sesión cerrada correctamente');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      // Limpiar estado local aunque falle la petición al servidor
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      showError('Error al cerrar sesión');
    }
  }, [showSuccess, showError]);

  /**
   * Refrescar token
   */
  const refreshToken = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No hay refresh token disponible');
      }

      const data = await authService.refreshToken(refreshToken);

      dispatch({
        type: AUTH_ACTIONS.REFRESH_TOKEN_SUCCESS,
        payload: data,
      });

      return data;
    } catch (error) {
      console.error('Error al refrescar token:', error);
      // Si falla el refresh, cerrar sesión
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      throw error;
    }
  }, []);

  /**
   * Actualizar usuario actual
   */
  const updateCurrentUser = useCallback(async () => {
    try {
      const user = await authService.getCurrentUser();
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: user,
      });
      return user;
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      showError('Error al actualizar datos del usuario');
      throw error;
    }
  }, [showError]);

  /**
   * Limpiar error
   */
  const clearError = useCallback(() => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  }, []);

  const value = {
    // Estado
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    error: state.error,

    // Funciones
    login,
    logout,
    refreshToken,
    updateCurrentUser,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook personalizado
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;
